import { invoke } from "@tauri-apps/api/core";

type AtlasInfo = {
	port: number;
	accessToken: string;
	isRunning: boolean;
};

export const getAtlasInfo = async () => {
	const atlasInfo: AtlasInfo = await invoke("get_atlas_info");
	if (atlasInfo === undefined || atlasInfo === null) {
		throw new Error("unexpected null result from get_atlas_info");
	}

	if (!atlasInfo.isRunning) {
		throw new Error("Atlas is not running");
	}

	return atlasInfo;
};

export const atlasFetch = async (
	url: string | URL | globalThis.Request,
	opts?: RequestInit
): Promise<Response> => {
	const options = opts || {};

	const atlasInfo = await getAtlasInfo();

	const port = atlasInfo.port;
	const token = atlasInfo.accessToken;

	const baseUrl = `http://localhost:${port}`;

	const requestInit = {
		...options,
		headers: {
			...options?.headers,
			"Cache-Control": "no-cache", // this is nice for debugging. we probably want to turn it off for expensive requests.
			"Atlas-Instance-AccessToken": token, // this header has to be here so do not overwrite.
		},
	};

	try {
		const resp = await fetch(baseUrl + url, requestInit);
		return resp;
	} catch (e) {
		if (e instanceof Error) {
			throw new Error("Proxied request failed");
		} else {
			throw e;
		}
	}
};

export const createAtlasProxyFetch = function (): (
	url: string | URL | globalThis.Request,
	opts?: RequestInit
) => Promise<Response> {
	return atlasFetch;
};

// ******************
// The stuff below is prototype code.
// We probably want to do this in the Rust layer in the future if this is a better experience for users
// when using Atlas as a proxy. But for now, this is the quickest implementation to try it out and get feedback.
// ******************

const getUrl = (input: string | URL | globalThis.Request): string => {
	if (typeof input === "string") {
		return input;
	}
	if (input instanceof URL) {
		return input.toString();
	}
	if (input instanceof Request) {
		return input.url;
	}
	return "";
};

const isHttpRequest = (url: string): boolean => {
	return url.startsWith("http://") || url.startsWith("https://");
};

export const newFetch = (settings, originalFetch) => {
	return (input: string | URL | globalThis.Request, init?: RequestInit): Promise<Response> => {
		try {
			const url = getUrl(input);

			if (
				settings.get("proxy_github") &&
				settings.get("proxy_github").enabled &&
				url.startsWith("https://api.github.com")
			) {
				return proxyGithubFetch(input, init);
			}

			if (
				settings.get("proxy_aws") &&
				settings.get("proxy_aws").enabled &&
				isHttpRequest(url)
			) {
				const parsedUrl = new URL(url);
				if (parsedUrl.host.includes("amazonaws.com")) {
					return proxyAWSFetch(input, init);
				}
			}
		} catch (e) {
			// if we get an error here, just default to using the original fetch.
			console.debug("Error in newFetch", e);
		}

		return originalFetch(input, init);
	};
};

const readStream = async (stream) => {
	const reader = stream.getReader();
	const decoder = new TextDecoder();
	try {
		let newBody = "";
		let done = false;
		while (!done) {
			const contents = await reader.read();
			newBody += decoder.decode(contents.value, { stream: true });
			done = contents.done;
		}
		return newBody;
	} finally {
		// we should release the read lock, as getReader() will lock the stream.
		// see https://developer.mozilla.org/en-US/docs/Web/API/ReadableStreamDefaultReader/releaseLock
		reader.releaseLock();
	}
};

export const proxyAWSFetch = async (req, init) => {
	// AWS fetches with Request Object.
	if (!(req instanceof Request)) {
		throw new Error("AWS Proxy expected Request object");
	}

	const parsedUrl = new URL(req.url);

	const copyReq = req.clone();
	const originalHost = parsedUrl.host;
	const atlasPath = "/v1/apis/aws" + parsedUrl.pathname;

	const atlasInfo = await getAtlasInfo();
	const port = atlasInfo.port;
	const token = atlasInfo.accessToken;
	const baseUrl = `http://localhost:${port}`;
	copyReq.headers.set("Atlas-Apis-Aws-Original-Hostname", originalHost);
	copyReq.headers.set("Atlas-Apis-Aws-Profile", "moment");
	copyReq.headers.set("Atlas-Instance-AccessToken", token);
	copyReq.headers.set("Cache-Control", "no-cache");

	let newBody;

	if (copyReq.body !== null && copyReq.body instanceof ReadableStream) {
		// the AWS client usually sends body as a stream.
		newBody = await readStream(copyReq.body);
	} else {
		// the AWS client usually sends body as a string.
		newBody = copyReq.body;
	}

	try {
		// I was getting "NotSupportedError: ReadableStream uploading is not supported" errors in Webkit.
		// Googling around, found this workaround - despite being super old -
		// https://github.com/GoogleChrome/workbox/issues/1732#issuecomment-432705129
		//
		// Reading in the entire stream and sending it in the body of the init object seems to work.
		// I am sure more complex AWS requests will break this, but lets try it out and see.
		const resp = await fetch(baseUrl + atlasPath, {
			body: newBody ? newBody : init?.body,
			headers: copyReq.headers ? copyReq.headers : init?.headers,
			method: copyReq.method ? copyReq.method : init?.method,
			cache: copyReq.cache ? copyReq.cache : init?.cache,
			referrer: copyReq.referrer ? copyReq.referrer : init?.referrer,
			referrerPolicy: copyReq.referrerPolicy ? copyReq.referrerPolicy : init?.referrerPolicy,
			mode: copyReq.mode ? copyReq.mode : init?.mode,
			credentials: copyReq.credentials ? copyReq.credentials : init?.credentials,
			redirect: copyReq.redirect ? copyReq.redirect : init?.redirect,
			integrity: copyReq.integrity ? copyReq.integrity : init?.integrity,
			keepalive: copyReq.keepalive ? copyReq.keepalive : init?.keepalive,
			signal: copyReq.signal ? copyReq.signal : init?.signal,
		});
		return resp;
	} catch (e) {
		if (e instanceof Error) {
			console.error("Proxied request failed", e);
			throw new Error("Proxied request failed");
		} else {
			throw e;
		}
	}
};

export const proxyGithubFetch = async (url, init) => {
	const atlasUrl = url.replace("https://api.github.com", "/v1/apis/http/github");
	return atlasFetch(atlasUrl, init);
};
